import { CmcWebApi, IWebApiRequestBody } from '@cmctechnology/webinvest-api-client-web-trading';
import { frontendActions, getConfig, LocationType, ResourceKey } from '@cmctechnology/webinvest-store-frontend';
import { Dispatch } from '@reduxjs/toolkit';
import { ACCOUNT_API_NAMES } from './apis';
import { makeConfirmationsKey, makeConfirmationsParamKey, makeConfirmationsParamSubscriptionKey } from './keys';
import { ACCOUNT_STORE_NAME, actions, IStore } from './store';
import { AccountKey, ConfirmationsParamKey, Sort } from './types';

export const fetchConfirmationsCount = async (dispatch: Dispatch<any>, getState: () => IStore, accountNo: AccountKey, from: Date, to: Date) => {
  const hash = getState()[ACCOUNT_STORE_NAME].confirmations[accountNo]?.from[from.valueOf()].to[to.valueOf()].hash ?? 0;
  const request: CmcWebApi.Account.AccountDateRangeRequest1 & IWebApiRequestBody = {
    AccountNumber: accountNo,
    StartDate: from,
    EndDate: to,
    _config: {
      hash
    }
  };

  try {
    const response = await frontendActions.webTradingApiCall<CmcWebApi.Shared.RecordCountModel1>(
      ACCOUNT_STORE_NAME,
      ACCOUNT_API_NAMES.ConfirmationsCount1,
      request,
      dispatch,
      makeConfirmationsKey(accountNo, from, to),
      false,
      true
    );
    const responseHash = response._apiInfo?.Hash ?? 0;
    dispatch(
      actions.confirmationsCountFetched({
        accountNo,
        from: from.valueOf(),
        to: to.valueOf(),
        count: response.RecordCount,
        hash: responseHash
      })
    );
    return hash === 0 || hash !== responseHash;
  } catch {
    return true;
  }
};

export const fetchConfirmations = (accountNo: AccountKey, sortColumn: string, sortDirection: Sort, from: Date, to: Date, pageSize: number, page: number) => {
  return async (dispatch: Dispatch<any>, getState: () => IStore) => {
    // fetch the count
    const shouldUpdate = await fetchConfirmationsCount(dispatch, getState, accountNo, from, to);
    // if should update clear all data for account/date range
    if (shouldUpdate) {
      dispatch(
        actions.confirmationsClear({
          accountNo,
          from: from.valueOf(),
          to: to.valueOf()
        })
      );
    }

    const paramsKey = makeConfirmationsParamKey(pageSize, sortColumn, sortDirection);
    // fetch existing data for params
    const exists = getState()[ACCOUNT_STORE_NAME].confirmations[accountNo]?.from[from.valueOf()]?.to[to.valueOf()]?.params[paramsKey];
    // if exists then don't need to fetch again
    if (exists?.pages[page]) return;

    const request: Partial<CmcWebApi.Account.ConfirmationsGetRequest2> & IWebApiRequestBody = {
      AccountNumber: accountNo,
      StartDate: from,
      EndDate: to,
      PageNumber: page,
      PageSize: pageSize,
      OrderBy: `${sortColumn} ${sortDirection}`
    };

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApi.Account.ConfirmationsPageModel3>(
        ACCOUNT_STORE_NAME,
        ACCOUNT_API_NAMES.ConfirmationsGet3,
        request,
        dispatch,
        makeConfirmationsParamSubscriptionKey(accountNo, from, to, pageSize, sortColumn, sortDirection, page),
        false,
        true
      );
      dispatch(
        actions.confirmationsPageFetched({
          accountNo,
          sortColumn,
          sortDirection,
          from: from.valueOf(),
          to: to.valueOf(),
          pageSize,
          page,
          items: response.Confirmations
        })
      );
    } catch {}
  };
};

export const onPollConfirmations =
  (subscriptions: ResourceKey[] = []) =>
  (dispatch: Dispatch<any>) => {
    subscriptions.forEach((sub) => {
      const args = JSON.parse(sub as string) as ConfirmationsParamKey;
      dispatch<any>(fetchConfirmations(args.accountNo, args.sortColumn, args.sortDirection, new Date(args.from), new Date(args.to), args.pageSize, args.page));
    });
  };

export const subscribeConfirmations =
  (location: LocationType, accountNo: AccountKey, sortColumn: string, sortDirection: Sort, from: Date, to: Date, pageSize: number, page: number) =>
  (dispatch: Dispatch<any>) => {
    dispatch<any>(
      frontendActions.subscribe({
        onPoll: onPollConfirmations,
        location,
        apiName: ACCOUNT_API_NAMES.ConfirmationsGet3,
        storeName: ACCOUNT_STORE_NAME,
        resourceKeys: [makeConfirmationsParamSubscriptionKey(accountNo, from, to, pageSize, sortColumn, sortDirection, page)],
        pollTimeout: getConfig().pollTimeout
      })
    );
  };

export const unsubscribeConfirmations =
  (location: LocationType, accountNo: AccountKey, sortColumn: string, sortDirection: Sort, from: Date, to: Date, pageSize: number, page: number) =>
  (dispatch: Dispatch<any>) => {
    dispatch<any>(
      frontendActions.unsubscribe({
        location,
        apiName: ACCOUNT_API_NAMES.ConfirmationsGet3,
        storeName: ACCOUNT_STORE_NAME,
        resourceKeys: [makeConfirmationsParamSubscriptionKey(accountNo, from, to, pageSize, sortColumn, sortDirection, page)]
      })
    );
  };
